import i18n from "@/shared/i18n/config";
import { API_ENDPOINTS, API_BASE_URL, getAuthRefreshUrl, setApiClientAuthResolver } from "@/shared/apiClient";
import { log } from "@/shared/utils/logger";
import { storage } from "@/shared/utils/storage";
import type {
  UserResponse,
  LoginRequest,
  LoginResponse,
  SignupRequest,
  PaymentCheckoutResponse,
  PaymentVerificationResponse,
  PaymentReadinessResponse,
  SelfServicePlanState,
  SelfServicePlanTier,
  TossBillingIssueResponse,
} from "@/shared/apiTypes";
import {
  getTokenExpiresIn,
  isTokenExpired,
  validateTokenFormat,
} from "../utils/jwtUtils";
import {
  saveTokens,
  updateTokens,
  loadTokens,
  clearTokens,
  getStorageType,
} from "../utils/tokenStorage";
import { registerAuthRuntimeProvider } from "./authRuntime";
import { TokenRefreshTimer } from "../utils/tokenRefreshTimer";
import {
  hasPermission as checkPermission,
  hasProjectPermission as checkProjectPermission,
} from "../utils/permissionChecker";
import { getAdminViewAsHeaders } from "./adminViewAs";
import {
  auditRequestHeaders,
  beginAuditRequest,
  createAuditRequestId,
  endAuditRequest,
  setAuditAuthHeadersProvider,
} from "@/shared/audit/auditClient";

export interface User {
  id: string;
  username: string;
  email: string;
  role: "admin" | "employee" | "client" | "partner";
  godMode?: boolean;
  // Account admin, or an active org_admin/platform_admin membership.
  organizationAuthority?: boolean;
  permissions?: Record<string, boolean>; // Flat permissions from JWT
  projects?: ProjectScope[]; // Project access from JWT
  selfServicePlan?: SelfServicePlanState | null;
}

export interface ProjectScope {
  project_id?: string;
  id?: string;
  role: string;
  view_permissions?: Record<string, boolean>;
  viewPermissions?: Record<string, boolean>;
  available_views?: string[];
  availableViews?: string[];
}

export interface AdminViewAsUserOption {
  id: string;
  username: string;
  email: string;
  role: User["role"];
  god_mode?: boolean;
  godMode?: boolean;
  is_active: boolean;
}

function normalizeProjectScopes(projects?: ProjectScope[]): ProjectScope[] | undefined {
  if (!Array.isArray(projects)) return undefined;

  return projects
    .map((project) => {
      const projectId = project.project_id ?? project.id;
      if (!projectId) return null;
      return {
        ...project,
        project_id: projectId,
        role: project.role,
      };
    })
    .filter((project): project is NonNullable<typeof project> => project !== null);
}

export interface LoginCredentials {
  username: string;
  password: string;
  rememberMe?: boolean;
}

export interface SignupCredentials {
  username?: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  selectedPlan?: SelfServicePlanTier;
  rememberMe?: boolean;
}

export interface AuthResponse {
  token: string; // Access token (15 minutes)
  refresh_token?: string; // Refresh token is httpOnly cookie-backed
  user: User;
  expires_in: number; // Access token expiry in seconds
}

export class AuthSessionError extends Error {
  readonly kind: "unauthorized" | "transient";
  readonly status?: number;

  constructor(
    kind: "unauthorized" | "transient",
    message: string,
    status?: number
  ) {
    super(message);
    this.name = "AuthSessionError";
    this.kind = kind;
    this.status = status;
  }
}

function formatApiErrorDetail(value: unknown): string | null {
  if (typeof value === "string") {
    return value.trim() || null;
  }

  if (Array.isArray(value)) {
    const messages = value
      .map((entry) => formatApiErrorDetail(entry))
      .filter((entry): entry is string => Boolean(entry));
    return messages.length > 0 ? messages.join("; ") : null;
  }

  if (value && typeof value === "object") {
    const detail = value as Record<string, unknown>;
    const message = formatApiErrorDetail(detail.msg ?? detail.message);
    if (!message) return null;

    const location = Array.isArray(detail.loc)
      ? detail.loc
          .filter((part) => typeof part === "string" || typeof part === "number")
          .map(String)
          .filter((part) => part !== "body")
          .join(".")
      : "";
    return location ? `${location}: ${message}` : message;
  }

  return null;
}

function apiErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== "object") return fallback;
  const error = payload as Record<string, unknown>;
  return formatApiErrorDetail(error.detail) ?? formatApiErrorDetail(error.error) ?? fallback;
}

// Convert UserResponse to User interface
function mapUserResponseToUser(userResponse: UserResponse): User {
  const rawPlan = userResponse.self_service_plan ?? userResponse.selfServicePlan ?? null;
  const selfServicePlan = rawPlan
    ? {
        ...rawPlan,
        selectedTier: rawPlan.selectedTier ?? rawPlan.selected_tier ?? rawPlan.tier,
        effectiveTier: rawPlan.effectiveTier ?? rawPlan.effective_tier ?? rawPlan.tier,
        paidTier: rawPlan.paidTier ?? rawPlan.paid_tier,
        priceLabel: rawPlan.priceLabel ?? rawPlan.price_label,
        billingUnit: rawPlan.billingUnit ?? rawPlan.billing_unit,
        quotaProfile: rawPlan.quotaProfile ?? rawPlan.quota_profile,
        plannedCapabilities: rawPlan.plannedCapabilities ?? rawPlan.planned_capabilities,
        billingStatus: rawPlan.billingStatus ?? rawPlan.billing_status ?? rawPlan.status,
        entitlementStatus: rawPlan.entitlementStatus ?? rawPlan.entitlement_status ?? rawPlan.status,
        billingMode: rawPlan.billingMode ?? rawPlan.billing_mode,
        isShadow: rawPlan.isShadow ?? rawPlan.is_shadow,
        checkoutReady: rawPlan.checkoutReady ?? rawPlan.checkout_ready,
        billingPortalReady: rawPlan.billingPortalReady ?? rawPlan.billing_portal_ready,
        entitlementsActive: rawPlan.entitlementsActive ?? rawPlan.entitlements_active,
        paymentProvider: rawPlan.paymentProvider ?? rawPlan.payment_provider,
        paymentOrderId: rawPlan.paymentOrderId ?? rawPlan.payment_order_id,
        paymentReadiness: rawPlan.paymentReadiness ?? rawPlan.payment_readiness,
        organizationAdminAvailable:
          rawPlan.organizationAdminAvailable ?? rawPlan.organization_admin_available,
        enterpriseContactSales: rawPlan.enterpriseContactSales ?? rawPlan.enterprise_contact_sales,
        updatedAt: rawPlan.updatedAt ?? rawPlan.updated_at,
      }
    : null;
  return {
    id: userResponse.id,
    username: userResponse.username,
    email: userResponse.email,
    role: userResponse.role,
    godMode: Boolean(userResponse.godMode ?? userResponse.god_mode),
    organizationAuthority: Boolean(userResponse.organizationAuthority ?? userResponse.organization_authority),
    permissions: userResponse.permissions, // Already flat from JWT
    projects: normalizeProjectScopes(userResponse.projects as ProjectScope[] | undefined),
    selfServicePlan,
  };
}

class AuthService {
  private accessToken: string | null = null;
  private user: User | null = null;
  private tokenRefreshTimer = new TokenRefreshTimer();
  private refreshPromise: Promise<string | null> | null = null;
  private reloadPromise: Promise<User | null> | null = null;
  private terminalUnauthorized = false;
  private sessionGeneration = 0;
  private pendingSignup: { key: string; intent: string; retryCreation?: boolean } | null = null;
  private pageLifecycleClosing = false;
  private refreshDeferred = false;
  private refreshStartDelayMs = 120;
  private authMutationTail: Promise<void> = Promise.resolve();
  private pendingLogout: { token: string | null; completed: boolean } | null = null;
  private logoutMarkerLoaded = false;

  private logoutStorageKey(): string {
    const browserOrigin = typeof window === "undefined" ? "http://localhost" : window.location.origin;
    return `pm-auth-logout-pending-v1:${new URL(API_BASE_URL || browserOrigin, browserOrigin).origin}`;
  }

  private getPendingLogout(): { token: string | null; completed: boolean } | null {
    if (!this.logoutMarkerLoaded) {
      this.logoutMarkerLoaded = true;
      try {
        if (typeof window !== "undefined" && localStorage.getItem(this.logoutStorageKey()) === "1") {
          this.pendingLogout = { token: null, completed: false };
        }
      } catch { /* Keep the in-memory intent when browser storage is unavailable. */ }
    }
    return this.pendingLogout;
  }

  private queueAuthMutation<T>(operation: () => Promise<T>): Promise<T> {
    // This tail owns transport lifetime and is never reset by clearAuth().
    // Queue callbacks use direct transports; they must not await queued refresh.
    const run = async (): Promise<T> => {
      if (typeof window !== "undefined" && typeof navigator !== "undefined" && navigator.locks?.request) {
        const origin = new URL(getAuthRefreshUrl(), window.location.origin).origin;
        return await navigator.locks.request(`pm-auth-cookie-mutation:${origin}`, operation);
      }
      // Unsupported browsers retain per-instance ordering only.
      return operation();
    };
    const result = this.authMutationTail.then(run, run);
    this.authMutationTail = result.then(() => undefined, () => undefined);
    return result;
  }

  private async performLogout(intent: { token: string | null; completed: boolean }): Promise<void> {
    // Earlier queued sign-ins may have established a newer browser credential.
    const token = this.accessToken || intent.token;
    this.clearAuth();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.auth.logout}`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(i18n.t("Server-side sign-out could not be confirmed"));
      intent.completed = true;
      if (this.pendingLogout === intent) {
        this.pendingLogout = null;
        try { localStorage.removeItem(this.logoutStorageKey()); } catch { /* Storage is best-effort. */ }
      }
    } catch {
      throw new Error(i18n.t("Server-side sign-out could not be confirmed"));
    } finally {
      clearTimeout(timeout);
    }
  }


  private get accessTokenExpiresInSeconds(): number {
    const token = this.accessToken;
    if (!token) return 900;
    const segments = token.split(".");
    if (segments.length !== 3) return 900;

    try {
      const normalizedPayload = segments[1]
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(Math.ceil(segments[1].length / 4) * 4, "=");
      const payload = JSON.parse(atob(normalizedPayload));
      const exp = Number(payload?.exp);
      const iat = Number(payload?.iat);
      if (Number.isFinite(exp) && Number.isFinite(iat) && exp > iat) {
        return Math.max(Math.floor(exp - iat), 30);
      }
    } catch {
      // Best-effort only; fall back to the canonical default.
    }

    return 900;
  }

  // Clear all auth data
  private clearAuth(): void {
    this.sessionGeneration += 1;
    this.refreshPromise = null;
    this.reloadPromise = null;
    this.accessToken = null;
    this.user = null;
    this.refreshDeferred = false;
    this.tokenRefreshTimer.clear();
    clearTokens();
  }

  private dispatchAuthEvent(eventName: "auth-updated" | "auth-invalidated"): void {
    if (typeof window === "undefined") {
      return;
    }
    window.dispatchEvent(new CustomEvent(eventName));
  }

  private markTerminalUnauthorized(): void {
    const wasTerminalUnauthorized = this.terminalUnauthorized;
    this.terminalUnauthorized = true;
    this.clearAuth();
    if (!wasTerminalUnauthorized) {
      this.dispatchAuthEvent("auth-invalidated");
    }
  }

  // Validate token format (public wrapper)
  public validateTokenFormat(token: string): boolean {
    return validateTokenFormat(token);
  }

  constructor() {
    // Load non-secret session state on initialization.
    const tokens = loadTokens();
    this.accessToken = null;
    this.user = tokens.user
      ? {
          ...tokens.user,
          projects: normalizeProjectScopes(tokens.user.projects),
        }
      : null;
    this.installPageLifecycleGuards();
  }

  private installPageLifecycleGuards(): void {
    if (typeof window === "undefined") {
      return;
    }

    const markClosing = () => {
      this.pageLifecycleClosing = true;
      this.tokenRefreshTimer.clear();
    };
    const markActive = () => {
      this.pageLifecycleClosing = false;
      if (this.terminalUnauthorized || this.getPendingLogout()) return;
      if (this.refreshDeferred || (this.user && !this.getToken())) {
        const generation = this.sessionGeneration;
        void this.refreshAccessToken().then(token => {
          if (token && generation === this.sessionGeneration && !this.pageLifecycleClosing) {
            this.dispatchAuthEvent("auth-updated");
          }
        }).catch(error => {
          if (!this.refreshDeferred && !this.pageLifecycleClosing && generation === this.sessionGeneration) {
            log.warn("AuthService", "Session renewal on page activation failed:", error);
          }
        });
      } else if (this.accessToken && this.user) {
        this.tokenRefreshTimer.setupRefresh(
          getTokenExpiresIn(this.accessToken) ?? this.accessTokenExpiresInSeconds,
          () => this.refreshAccessToken()
        );
      }
    };

    window.addEventListener("pagehide", markClosing);
    window.addEventListener("beforeunload", markClosing);
    window.addEventListener("pageshow", markActive);
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        markClosing();
        return;
      }
      markActive();
    });
  }

  private async canStartRefreshRequest(): Promise<boolean> {
    if (this.pageLifecycleClosing) {
      return false;
    }
    if (typeof window === "undefined") {
      return true;
    }

    await new Promise((resolve) => window.setTimeout(resolve, this.refreshStartDelayMs));
    return !this.pageLifecycleClosing && document.visibilityState !== "hidden";
  }

  private deferRefreshUntilActive(): string | null {
    this.refreshDeferred = true;
    const token = this.getToken();
    if (token) return token;
    // Inactivity is not a server rejection or proof that the cookie is absent.
    if (this.accessToken || this.user) {
      throw new AuthSessionError("transient", "Session renewal is deferred until the page is active");
    }
    return null;
  }

  private shouldSkipAnonymousLoginRefreshProbe(): boolean {
    if (typeof window === "undefined") {
      return false;
    }

    const path = window.location.pathname.replace(/\/+$/, "") || "/";
    return !this.user && (path === "/login" || path === "/signup");
  }

  private updateSessionFromPayload(data: {
    token?: string;
    user?: UserResponse | User;
    expires_in?: number;
  }): User | null {
    if (data.token) {
      this.accessToken = data.token;
      this.refreshDeferred = false;
    }

    if (data.user) {
      const normalizedUser =
        "role" in data.user && "username" in data.user && "id" in data.user
          ? mapUserResponseToUser(data.user as UserResponse)
          : (data.user as User);
      this.user = normalizedUser;
    }

    if (this.accessToken && this.user) {
      this.terminalUnauthorized = false;
      updateTokens(this.accessToken, "", this.user);
      this.tokenRefreshTimer.setupRefresh(
        data.expires_in || this.accessTokenExpiresInSeconds,
        () =>
          this.refreshAccessToken()
      );
      return this.user;
    }

    return this.user;
  }

  private scheduleTransientRefreshRetry(): void {
    if (!this.user || this.terminalUnauthorized || this.refreshDeferred || this.pageLifecycleClosing) return;
    this.tokenRefreshTimer.setupRefresh(60, () =>
        this.refreshAccessToken()
    );
  }

  private applyAuthResponse(data: LoginResponse, rememberSession: boolean): AuthResponse {
    if (!data.token || !data.user) {
      throw new Error("Invalid login response");
    }

    this.sessionGeneration += 1;
    this.refreshPromise = null;
    this.reloadPromise = null;
    this.terminalUnauthorized = false;
    this.accessToken = data.token;
    this.user = mapUserResponseToUser(data.user);

    saveTokens(
      this.accessToken,
      "",
      this.user,
      rememberSession
    );

    this.tokenRefreshTimer.setupRefresh(
      data.expires_in || this.accessTokenExpiresInSeconds,
      () =>
      this.refreshAccessToken()
    );

    return {
      token: this.accessToken,
      user: this.user,
      expires_in: data.expires_in || this.accessTokenExpiresInSeconds,
    };
  }

  login(credentials: LoginCredentials): Promise<AuthResponse> {
    const pendingLogout = this.getPendingLogout();
    return this.queueAuthMutation(async () => {
      if (pendingLogout && !pendingLogout.completed) await this.performLogout(pendingLogout);
      return this.performLogin(credentials);
    });
  }

  private async performLogin(credentials: LoginCredentials): Promise<AuthResponse> {
    this.pendingSignup = null;
    this.terminalUnauthorized = false;
    const loginRequest: LoginRequest = {
      username: credentials.username,
      password: credentials.password,
      rememberMe: credentials.rememberMe,
    };

    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.auth.login}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(loginRequest),
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(apiErrorMessage(error, "Login failed"));
    }

    const data: LoginResponse = await response.json();

    return this.applyAuthResponse(data, credentials.rememberMe || false);
  }

  signup(credentials: SignupCredentials): Promise<AuthResponse> {
    const pendingLogout = this.getPendingLogout();
    return this.queueAuthMutation(async () => {
      if (pendingLogout && !pendingLogout.completed) await this.performLogout(pendingLogout);
      return this.performSignup(credentials);
    });
  }

  private async performSignup(credentials: SignupCredentials): Promise<AuthResponse> {
    this.terminalUnauthorized = false;
    const signupRequest: SignupRequest = {
      email: credentials.email,
      password: credentials.password,
      first_name: credentials.firstName,
      last_name: credentials.lastName,
      selected_plan: credentials.selectedPlan,
      rememberMe: credentials.rememberMe,
    };
    if (credentials.username?.trim()) {
      signupRequest.username = credentials.username.trim();
    }

    // Keep only non-secret intent and the request key in memory. Recovery
    // rechecks the supplied password, including a corrected retry password.
    const { password: _password, ...publicIntent } = signupRequest;
    const intent = JSON.stringify(publicIntent);
    const existing = this.pendingSignup?.intent === intent ? this.pendingSignup : null;
    const key = existing?.key ?? Array.from(crypto.getRandomValues(new Uint8Array(16)), value => value.toString(16).padStart(2, '0')).join('');
    const attempt: NonNullable<typeof this.pendingSignup> = existing ?? { key, intent };
    this.pendingSignup = attempt;
    signupRequest.signup_request_id = attempt.key;
    const send = (recover: boolean) => fetch(`${API_BASE_URL}${API_ENDPOINTS.auth.signup}${recover ? '/recover' : ''}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(signupRequest), credentials: 'include',
    });
    if (!existing || existing.retryCreation) {
      const reconcilingRetry = Boolean(existing);
      if (existing) existing.retryCreation = false;
      let response: Response | null = null;
      try { response = await send(false); } catch { /* Reconcile an uncertain creation once. */ }
      if (response && !response.ok && response.status < 500 && !(reconcilingRetry && response.status === 409)) {
        this.pendingSignup = null;
        const error = await response.json().catch(() => ({}));
        throw new Error(apiErrorMessage(error, 'Signup failed'));
      }
      if (response?.ok) {
        let parsed: LoginResponse | null = null;
        try { parsed = await response.json(); } catch { /* Reconcile a truncated response. */ }
        if (parsed?.token && parsed.user?.id) {
          const result = this.applyAuthResponse(parsed, credentials.rememberMe ?? true);
          this.pendingSignup = null;
          return result;
        }
      }
    }
    const recovered = await send(true);
    if (!recovered.ok) {
      // A later explicit retry may submit the same key when no receipt exists.
      // A late original commit then conflicts and is reconciled with that key.
      if (recovered.status === 404) attempt.retryCreation = true;
      const error = await recovered.json().catch(() => ({}));
      throw new Error(apiErrorMessage(error, 'Signup recovery is unavailable. Try again or sign in.'));
    }
    const data: LoginResponse = await recovered.json();
    if (data.user?.email?.toLowerCase() !== credentials.email.trim().toLowerCase()) {
      throw new Error('Signup recovery returned a different account');
    }
    const result = this.applyAuthResponse(data, credentials.rememberMe ?? true);
    this.pendingSignup = null;
    return result;
  }

  async updateSelfServicePlan(selectedPlan: SelfServicePlanTier): Promise<AuthResponse> {
    const generation = this.sessionGeneration;
    const ownerId = this.user?.id;
    const rememberMe = getStorageType() === "local";
    const requireCurrentSession = () => {
      if (generation !== this.sessionGeneration || ownerId !== this.user?.id || this.getPendingLogout()) {
        throw new AuthSessionError("transient", "Session changed before the plan response could be applied");
      }
    };
    const response = await this.authenticatedFetch(API_ENDPOINTS.auth.selfServicePlan, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ selected_plan: selectedPlan }),
    });
    requireCurrentSession();

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      requireCurrentSession();
      throw new Error(apiErrorMessage(error, "Failed to update plan"));
    }

    const data: LoginResponse = await response.json();
    requireCurrentSession();
    return this.applyAuthResponse(data, rememberMe);
  }

  async getPaymentReadiness(): Promise<PaymentReadinessResponse> {
    const response = await this.authenticatedFetch(API_ENDPOINTS.payments.readiness);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(apiErrorMessage(error, "Failed to load payment readiness"));
    }
    return response.json();
  }

  async verifyPaymentReadiness(): Promise<PaymentVerificationResponse> {
    const response = await this.authenticatedFetch(API_ENDPOINTS.payments.readinessVerify);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(apiErrorMessage(error, "Failed to verify payment readiness"));
    }
    return response.json();
  }

  async createPaymentCheckout(
    planTier: Exclude<SelfServicePlanTier, "free">,
    provider?: string
  ): Promise<PaymentCheckoutResponse> {
    const response = await this.authenticatedFetch(API_ENDPOINTS.payments.checkout, {
      method: "POST",
      body: JSON.stringify({
        plan_tier: planTier,
        provider,
      }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(apiErrorMessage(error, "Checkout is not ready"));
    }
    return response.json();
  }

  async createBillingPortal(): Promise<PaymentCheckoutResponse> {
    const response = await this.authenticatedFetch(API_ENDPOINTS.payments.billingPortal, {
      method: "POST",
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(apiErrorMessage(error, "Billing portal is not ready"));
    }
    return response.json();
  }

  async issueTossBillingKey(params: {
    authKey: string;
    customerKey: string;
    orderId: string;
  }): Promise<TossBillingIssueResponse> {
    const response = await this.authenticatedFetch(API_ENDPOINTS.payments.tossBillingIssue, {
      method: "POST",
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(apiErrorMessage(error, "Toss billing authorization could not be completed"));
    }
    return response.json();
  }

  logout(): Promise<void> {
    this.pendingSignup = null;
    this.logoutMarkerLoaded = true;
    const intent = { token: this.accessToken, completed: false };
    this.pendingLogout = intent;
    try { localStorage.setItem(this.logoutStorageKey(), "1"); } catch { /* Keep the in-memory intent. */ }
    this.terminalUnauthorized = false;
    this.clearAuth();
    storage.clearProjectCache();
    return this.queueAuthMutation(() => this.performLogout(intent));
  }

  async refreshAccessToken(): Promise<string | null> {
    if (this.getPendingLogout()) return null;
    if (this.terminalUnauthorized) {
      throw new AuthSessionError(
        "unauthorized",
        "Session is no longer authorized"
      );
    }

    if (this.pageLifecycleClosing) {
      return this.deferRefreshUntilActive();
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const generation = this.sessionGeneration;
    const refreshTask: Promise<string | null> = this.queueAuthMutation(async () => {
      let invalidatedCurrentSession = false;
      let collisionRetried = false;
      try {
        if (generation !== this.sessionGeneration) return this.getToken();
        if (!(await this.canStartRefreshRequest())) {
          if (generation !== this.sessionGeneration) return this.getToken();
          return this.deferRefreshUntilActive();
        }
        if (generation !== this.sessionGeneration) return this.getToken();
        this.refreshDeferred = false;

        const fetchRefresh = () => fetch(
          getAuthRefreshUrl(),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({}),
            credentials: "include",
          }
        );

        let response = await fetchRefresh();
        if (response.status === 409 && generation === this.sessionGeneration) {
          // Exactly one fast retry, including anonymous boot. A 30-second
          // timer can turn a collision into a server-side replay revocation.
          collisionRetried = true;
          this.tokenRefreshTimer.clear();
          const retryDeadline = Date.now() + 5000;
          await new Promise(resolve => setTimeout(resolve, 1000));
          if (generation !== this.sessionGeneration || this.getPendingLogout()) return this.getToken();
          if (this.pageLifecycleClosing || (typeof document !== "undefined" && document.visibilityState === "hidden")) {
            return this.deferRefreshUntilActive();
          }
          if (Date.now() > retryDeadline) {
            throw new AuthSessionError("transient", "Refresh collision retry window elapsed", 409);
          }
          // Resend no token in JSON: fetch reads the current HttpOnly cookie.
          response = await fetchRefresh();
        }
        if (generation !== this.sessionGeneration) return this.getToken();
        if (!response.ok) {
          const kind = response.status === 401 ? "unauthorized" : "transient";
          if (kind === "unauthorized") {
            invalidatedCurrentSession = true;
            this.markTerminalUnauthorized();
          }
          throw new AuthSessionError(
            kind,
            kind === "unauthorized"
              ? "Refresh token is missing, invalid, or expired"
              : `Refresh request failed with status ${response.status}`,
            response.status
          );
        }

        if (response.status === 204) {
          return null;
        }

        const data = await response.json();
        if (generation !== this.sessionGeneration) return this.getToken();
        this.updateSessionFromPayload(data);
        log.info("AuthService", "Access token refreshed successfully");
        return this.accessToken;
      } catch (error) {
        if (generation !== this.sessionGeneration && !invalidatedCurrentSession) return this.getToken();
        if (error instanceof AuthSessionError) {
          if (error.kind === "transient" && !collisionRetried) {
            this.scheduleTransientRefreshRetry();
          }
          throw error;
        }
        if (!collisionRetried) this.scheduleTransientRefreshRetry();
        throw new AuthSessionError(
          "transient",
          error instanceof Error ? error.message : "Failed to refresh token"
        );
      }
    }).finally(() => {
      // Also release single-flight state if Web Lock acquisition itself fails.
      if (this.refreshPromise === refreshTask) this.refreshPromise = null;
    });

    this.refreshPromise = refreshTask;
    return refreshTask;
  }

  async getCurrentUser(): Promise<User | null> {
    if (!this.accessToken || this.terminalUnauthorized) return null;
    const generation = this.sessionGeneration;

    try {
      // The /auth/me endpoint now returns user from JWT without DB query
      const response = await this.authenticatedFetch(
        `${API_BASE_URL}${API_ENDPOINTS.auth.me}`,
        {
          method: "GET",
        }
      );

      log.debug("AuthService", "getCurrentUser response:", response);

      if (!response.ok) {
        throw new Error("Failed to get user info");
      }

      const data: UserResponse = await response.json();
      // Logout, invalidation, or a new sign-in owns the session after this request.
      if (generation !== this.sessionGeneration) return null;
      this.user = mapUserResponseToUser(data);

      // Save updated user data
      updateTokens(this.accessToken, "", this.user);

      return this.user;
    } catch (error) {
      log.error("AuthService", "getCurrentUser error:", error);
      return null;
    }
  }

  async listAdminViewAsUsers(): Promise<AdminViewAsUserOption[]> {
    const response = await this.authenticatedFetch(API_ENDPOINTS.admin.viewAsUsers, {
      method: "GET",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(apiErrorMessage(error, "Failed to load users"));
    }

    return response.json();
  }

  async getAdminViewAsUser(userId: string): Promise<User> {
    const response = await this.authenticatedFetch(API_ENDPOINTS.admin.viewAsUserById(userId), {
      method: "GET",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const viewAsError = new Error(apiErrorMessage(error, "Failed to load selected user"));
      (viewAsError as Error & { status?: number }).status = response.status;
      throw viewAsError;
    }

    const data: UserResponse = await response.json();
    return mapUserResponseToUser(data);
  }

  async reloadSession(): Promise<User | null> {
    if (this.getPendingLogout()) {
      await this.queueAuthMutation(async () => {
        const pending = this.getPendingLogout();
        if (pending && !pending.completed) await this.performLogout(pending);
      });
      return null;
    }
    if (this.terminalUnauthorized) return null;
    if (this.reloadPromise) return this.reloadPromise;

    const generation = this.sessionGeneration;
    const isCurrentSession = () => generation === this.sessionGeneration;
    const performReload = async (token: string): Promise<User | null> => {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.auth.reload}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!isCurrentSession()) return this.user;
      if (!response.ok) {
        const kind = response.status === 401 ? "unauthorized" : "transient";
        throw new AuthSessionError(kind, kind === "unauthorized"
          ? "Session is no longer authorized"
          : `Session reload failed with status ${response.status}`, response.status);
      }
      const data = await response.json();
      if (!isCurrentSession()) return this.user;
      return this.updateSessionFromPayload(data);
    };

    // Defer the body so even an immediate anonymous return can safely release
    // its own promise. A completed cache miss must not poison a later login.
    const reloadTask: Promise<User | null> = Promise.resolve().then(async () => {
      try {
        let token = this.getToken();
        let attemptedRefresh = false;
        if (!token) {
          if (this.shouldSkipAnonymousLoginRefreshProbe()) return null;
          try {
            attemptedRefresh = true;
            token = await this.refreshAccessToken();
          } catch (error) {
            if (!isCurrentSession()) return this.user;
            if (error instanceof AuthSessionError && error.kind === "unauthorized") return null;
            throw error;
          }
        }
        if (!isCurrentSession()) return this.user;
        if (!token) {
          if (this.accessToken || this.user) this.markTerminalUnauthorized();
          return null;
        }
        try {
          return await performReload(token);
        } catch (error) {
          if (!isCurrentSession()) return this.user;
          if (error instanceof AuthSessionError && error.kind === "unauthorized" && !attemptedRefresh) {
            try {
              token = await this.refreshAccessToken();
              if (!isCurrentSession()) return this.user;
              if (!token) {
                // A rejected bearer plus no refresh session is terminal. A
                // standalone anonymous HTTP 204 does not revoke a valid bearer.
                this.markTerminalUnauthorized();
                return null;
              }
              return await performReload(token);
            } catch (refreshError) {
              if (!isCurrentSession()) return this.user;
              if (refreshError instanceof AuthSessionError && refreshError.kind === "unauthorized") {
                this.markTerminalUnauthorized();
                return null;
              }
              throw refreshError;
            }
          }
          if (error instanceof AuthSessionError && error.kind === "unauthorized") {
            this.markTerminalUnauthorized();
            return null;
          }
          throw error;
        }
      } finally {
        if (this.reloadPromise === reloadTask) this.reloadPromise = null;
      }
    });
    this.reloadPromise = reloadTask;
    return reloadTask;
  }

  getToken(): string | null {
    // Tokens live only in-memory — never read from web storage.
    if (!this.terminalUnauthorized && this.accessToken && !isTokenExpired(this.accessToken)) {
      return this.accessToken;
    }
    return null;
  }

  getUser(): User | null {
    return this.user;
  }

  getSessionIdentity(): string | null {
    return this.user ? `${this.sessionGeneration}:${this.user.id}` : null;
  }

  isAuthenticated(): boolean {
    return !this.getPendingLogout() && !this.terminalUnauthorized && !!this.getToken() && !!this.user;
  }

  // Check token validity and refresh if needed
  async ensureValidToken(): Promise<boolean> {
    return (await this.ensureValidTokenStatus()) === "valid";
  }

  /**
   * The server can expire a still-unexpired bearer, for example when an admin
   * changes a role or feedback setting that applies to themselves. Renew once
   * so the rejected request can be retried with the current authority.
   */
  async renewAfterUnauthorized(rejectedAuthorization: string | null): Promise<boolean> {
    if (this.getPendingLogout() || this.terminalUnauthorized || !this.user) return false;
    const current = this.getToken();
    if (current && rejectedAuthorization && rejectedAuthorization !== `Bearer ${current}`) {
      // Another request already renewed the session after this one was sent.
      return true;
    }
    try {
      const token = await this.refreshAccessToken();
      return !!token && !this.terminalUnauthorized && token !== current;
    } catch (error) {
      log.warn("AuthService", "Session renewal after an unauthorized response failed:", error);
      return false;
    }
  }

  async ensureValidTokenStatus(): Promise<"valid" | "unauthorized" | "transient"> {
    if (this.getPendingLogout()) return "unauthorized";
    if (this.terminalUnauthorized) {
      return "unauthorized";
    }

    if (!this.accessToken || isTokenExpired(this.accessToken)) {
      try {
        await this.refreshAccessToken();
        if (this.getToken()) return "valid";
        if (this.accessToken || this.user) this.markTerminalUnauthorized();
        return "unauthorized";
      } catch (error) {
        if (error instanceof AuthSessionError) {
          return error.kind;
        }
        // Token refresh failed; do not clear tokens automatically
        return "transient";
      }
    }

    return "valid";
  }

  hasPermission(
    resource: string,
    action: string,
    view: string | null = null
  ): boolean {
    return checkPermission(this.user, resource, action, view);
  }

  // Check project-level permissions
  hasProjectPermission(
    projectId: string,
    requiredRole: "owner" | "editor" | "viewer"
  ): boolean {
    return checkProjectPermission(this.user, projectId, requiredRole);
  }

  // Add auth header to any fetch request
  getAuthHeaders(): HeadersInit {
    // Always resolve the latest valid token from storage/memory
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Wrapper for authenticated API calls with auto token refresh
  async authenticatedFetch(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const isAbsolute = typeof url === "string" && /^https?:\/\//.test(url);
    const finalUrl = isAbsolute ? url : `${API_BASE_URL}${url}`;
    const browserOrigin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const configuredApiOrigin = new URL(API_BASE_URL || browserOrigin, browserOrigin).origin;
    const requestOrigin = new URL(finalUrl, configuredApiOrigin).origin;
    const isTrustedApiOrigin = requestOrigin === configuredApiOrigin;

    if (!isTrustedApiOrigin) {
      const headers = new Headers(options.headers);
      headers.delete("Authorization");
      headers.delete("X-PM-Admin-Act-As-User-Id");
      headers.delete("X-Request-ID");
      headers.delete("X-Audit-Session-ID");
      headers.delete("X-Audit-Device-ID");
      log.debug("AuthService", "External fetch without application credentials", {
        method: String(options.method || "GET").toUpperCase(),
      });
      return fetch(finalUrl, {
        ...options,
        headers,
        credentials: "omit",
      });
    }

    const sessionIdentity = this.getSessionIdentity();
    const viewAsIdentity = new Headers(getAdminViewAsHeaders(finalUrl)).get('x-pm-admin-act-as-user-id');
    const method = String(options.method || "GET").toUpperCase();
    const auditRequestId = createAuditRequestId();
    beginAuditRequest({ url: finalUrl, method, requestId: auditRequestId });

    const performRequest = async (): Promise<Response> => {
      const authStatus = await this.ensureValidTokenStatus();
      if ((sessionIdentity !== null && sessionIdentity !== this.getSessionIdentity())
        || viewAsIdentity !== new Headers(getAdminViewAsHeaders(finalUrl)).get('x-pm-admin-act-as-user-id')) {
        throw new AuthSessionError('transient', 'Authentication context changed. Retry this action.');
      }
      if (authStatus !== "valid") {
        throw new AuthSessionError(
          authStatus === "unauthorized" ? "unauthorized" : "transient",
          authStatus === "unauthorized" ? "Authentication required" : "Authentication is temporarily unavailable"
        );
      }

      const headers = new Headers(options.headers);
      const setHeaders = (source: HeadersInit) => {
        new Headers(source).forEach((value, key) => headers.set(key, value));
      };
      setHeaders(this.getAuthHeaders());
      setHeaders(getAdminViewAsHeaders(finalUrl));
      setHeaders(auditRequestHeaders(auditRequestId));

      const body = options.body;
      const hasExplicitContentType = headers.has("Content-Type");
      const shouldDefaultToJson =
        !!body &&
        !hasExplicitContentType &&
        !(body instanceof FormData) &&
        !(body instanceof URLSearchParams) &&
        !(body instanceof Blob) &&
        !(body instanceof ArrayBuffer);

      if (shouldDefaultToJson) {
        headers.set("Content-Type", "application/json");
      }

      log.debug("AuthService", "Authenticated fetch request", {
        method,
        headerCount: Array.from(new Headers(headers).keys()).length,
      });

      return fetch(finalUrl, {
        ...options,
        headers,
        credentials: "include",
      });
    };

    try {
      let response = await performRequest();
      if (response.status === 401 && this.user && !this.terminalUnauthorized) {
        try {
          await this.refreshAccessToken();
          response = await performRequest();
        } catch (error) {
          log.warn("AuthService", "authenticatedFetch refresh retry failed:", error);
        }
      }

      endAuditRequest({
        requestId: auditRequestId,
        url: finalUrl,
        method,
        status: response.status,
        ok: response.ok,
      });
      return response;
    } catch (error) {
      endAuditRequest({
        requestId: auditRequestId,
        url: finalUrl,
        method,
        ok: false,
        error,
      });
      throw error;
    }
  }
}

export const authService = new AuthService();

// Wire the authRuntime provider so other modules can access tokens
// without importing authService directly (avoids circular deps).
registerAuthRuntimeProvider({
  getAccessToken: () => authService.getToken(),
  getAuthHeaders: () => authService.getAuthHeaders(),
  getSessionIdentity: () => authService.getSessionIdentity(),
  ensureValidToken: () => authService.ensureValidToken(),
  ensureValidTokenStatus: () => authService.ensureValidTokenStatus(),
  renewAfterUnauthorized: (rejectedAuthorization) => authService.renewAfterUnauthorized(rejectedAuthorization),
});

/**
 * Module-level token accessor — replaces the former window.__authService global.
 * Safe to import from any module; the singleton is resolved at call-time so
 * circular-import ordering does not matter.
 */
export function getAuthToken(): string | null {
  return authService.getToken();
}

/**
 * Module-level auth-header accessor — mirrors authService.getAuthHeaders()
 * without requiring callers to hold a reference to the singleton.
 */
export function getAuthHeaders(): Record<string, string> {
  const token = authService.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Wire the auth-header resolver into apiClient so it never needs to import
// this module directly (which would create a circular dependency).
setApiClientAuthResolver(getAuthHeaders);
setAuditAuthHeadersProvider(getAuthHeaders, () => authService.getSessionIdentity() ? authService.getUser()?.id || null : null);
